import { useCallback, useEffect, useState } from 'react';

import { useAppSelector, useRole } from 'hooks';
import { AccessRoles } from 'constants/enums';
import { getCurrentGroup, isPrepaidOrg } from 'store/groups/reducer';
import { getBillingAgreementsStatus, postBillingAgreementsReaffirm } from 'store/billing/service';
import { isOrganization } from 'utils/Groups';

import { useBillingGid } from '../hooks';
import {
  emptyAgreementStore,
  getAgreementStore,
  IAgreementStore,
  isAgreementStoreCurrent,
  setAgreementStore,
  subscribeAgreementStore
} from './agreementStore';

const loadStatus = async (gid: string, aid: string) => {
  const store = getAgreementStore();
  if (isAgreementStoreCurrent(gid, aid) && (store.loading || store.status || store.error)) return;
  setAgreementStore({ ...emptyAgreementStore, gid, aid, loading: true });
  try {
    const status = await getBillingAgreementsStatus(gid);
    // A group switch or a different account mid-flight makes this answer irrelevant.
    if (isAgreementStoreCurrent(gid, aid)) setAgreementStore({ loading: false, status });
  } catch {
    if (isAgreementStoreCurrent(gid, aid)) setAgreementStore({ loading: false, error: true });
  }
};

/** Display-name key for a document type. Only `terms` and `baa` carry an acceptance. */
export const agreementDocumentLabelKey = (documentType: string): string => {
  return documentType === 'baa' ? 'billingAgreementBaaKey' : 'billingAgreementTermsKey';
};

export interface IAgreementStatus {
  /** True only when the re-affirmation can possibly apply, so nothing else ever fetches. */
  applicable: boolean;
  loading: boolean;
  error: boolean;
  items: IBillingAgreementStatusItem[];
  /** The ordinary case: the organization accepted the current versions, this account has not
   * confirmed them yet. A dismissible acknowledgement, never a gate. */
  needsAcknowledgement: boolean;
  /** A newer dated master supersedes what the organization accepted: a genuine re-acceptance,
   * which blocks the billing surfaces until it is given. */
  superseded: boolean;
  dismissed: boolean;
  saving: boolean;
  dismiss: () => void;
  reaffirm: () => Promise<boolean>;
}

/**
 * The agreement re-affirmation state for the signed-in billing admin. The request is made only
 * when the current group is a prepaid organization and the user holds billing access on it, so a
 * clinician on a non-DTP organization never triggers it.
 */
export const useAgreementStatus = (): IAgreementStatus => {
  const aid = useAppSelector((state) => state.oauth.data?.aid) || '';
  const currentGroup = useAppSelector(getCurrentGroup);
  const prepaid = useAppSelector(isPrepaidOrg);
  const hasBillingAccess = useRole([AccessRoles.BILLING]);
  const gid = useBillingGid();
  const isOrg = !!currentGroup && currentGroup.gid !== 'all' && isOrganization(currentGroup);
  const applicable = !!gid && !!aid && isOrg && prepaid && hasBillingAccess;

  const [snapshot, setSnapshot] = useState<IAgreementStore>(getAgreementStore());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const listener = () => setSnapshot(getAgreementStore());
    const unsubscribe = subscribeAgreementStore(listener);
    listener();
    return unsubscribe;
  }, []);

  useEffect(() => {
    if (applicable) loadStatus(gid, aid);
  }, [applicable, gid, aid]);

  const dismiss = useCallback(() => setAgreementStore({ dismissed: true }), []);

  const reaffirm = useCallback(async () => {
    if (!gid) return false;
    setSaving(true);
    try {
      const status = await postBillingAgreementsReaffirm(gid);
      if (isAgreementStoreCurrent(gid, aid)) setAgreementStore({ status, error: false });
      return true;
    } catch {
      return false;
    } finally {
      setSaving(false);
    }
  }, [gid, aid]);

  // The snapshot must match both, or a stale render shows one account's status to another.
  const isSnapshotCurrent = snapshot.gid === gid && snapshot.aid === aid;
  const status = isSnapshotCurrent ? snapshot.status : null;
  const items = status?.items || [];
  const superseded = !!status && items.some((item) => !item.organisationAccepted);

  return {
    applicable,
    loading: isSnapshotCurrent && snapshot.loading,
    error: isSnapshotCurrent && snapshot.error,
    items,
    needsAcknowledgement: !!status && !status.reaffirmationComplete && !superseded,
    superseded,
    dismissed: isSnapshotCurrent && snapshot.dismissed,
    saving,
    dismiss,
    reaffirm
  };
};
